import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Logo } from "@/components/ui/logo";
import { 
  CheckCircle, 
  Users, 
  FileText, 
  TrendingUp,
  ArrowRight,
  Clock,
  Sparkles,
  Award
} from "lucide-react";

const features = [
  {
    icon: Users,
    title: "Student Management",
    description: "Organize and track all your students in one place with detailed profiles and progress history.",
    color: "primary",
  },
  {
    icon: CheckCircle,
    title: "Assessment Scoring",
    description: "Complete CDRS assessments with guided scoring across all developmental domains.",
    color: "accent",
  },
  {
    icon: FileText,
    title: "Report Generation",
    description: "Generate comprehensive reports ready for IEP meetings and parent conferences.",
    color: "secondary",
  },
  {
    icon: TrendingUp,
    title: "Progress Tracking",
    description: "Monitor student progress over time with visual analytics and trend analysis.",
    color: "dayc-success",
  },
];

const benefits = [
  {
    icon: Clock,
    title: "Save ~50% Time",
    description: "Reduce assessment time with our streamlined workflow and automated calculations.",
  },
  {
    icon: Award,
    title: "Accurate Results",
    description: "Automated calculations ensure precise composite scores every time.",
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-muted/30 to-background">
      {/* Navigation */}
      <nav className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Logo size="md" linkTo="/" showText showTagline />
            
            {/* Nav Links */}
            <div className="hidden md:flex items-center gap-6">
              <a href="#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Features
              </a>
              <Link to="/pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Pricing
              </Link>
            </div>
            
            {/* CTAs */}
            <div className="flex items-center gap-3">
              <Link to="/login" className="hidden sm:block">
                <Button variant="ghost" size="sm">Sign In</Button>
              </Link>
              <Link to="/signup">
                <Button size="sm" className="gap-2 btn-press">
                  Get Started
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-20 pb-24 lg:pt-32 lg:pb-40 overflow-hidden">
        {/* Animated Background Decorations */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="blob blob-1" />
          <div className="blob blob-2" />
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-gradient-to-r from-primary/5 to-secondary/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '-2s' }} />
          <div className="absolute bottom-1/3 right-1/4 w-[400px] h-[400px] bg-gradient-to-l from-accent/5 to-primary/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '-6s' }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto container-padding">
          <div className="text-center max-w-4xl mx-auto">
            {/* Trust Badge */}
            <div className="animate-fade-in">
              <Badge variant="outline" className="trust-badge px-4 py-1.5 text-sm font-medium mb-6 hover-lift cursor-default">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                Professional Assessment Tools
              </Badge>
            </div>
            
            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6 tracking-tight animate-fade-in-up">
              Streamline Your
              <span className="gradient-text-animated block mt-2">Developmental Assessments</span>
            </h1>
            
            {/* Subheadline */}
            <p className="text-lg sm:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed animate-fade-in-up" style={{ animationDelay: '100ms' }}>
              The comprehensive CDRS platform that helps you track progress, generate reports, 
              and manage student development—all in one intuitive place.
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12 animate-fade-in-up" style={{ animationDelay: '200ms' }}>
              <Link to="/signup">
                <Button size="lg" className="gap-2 btn-press text-base px-8 shadow-lg shadow-primary/25 btn-shine animate-pulse-glow hover:scale-105 transition-transform">
                  Start Free Trial
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button size="lg" variant="outline" className="text-base px-8 hover-lift">
                  View Pricing
                </Button>
              </Link>
            </div>
            
            {/* Social Proof */}
            <div className="flex items-center justify-center gap-6 text-sm text-muted-foreground animate-fade-in-up" style={{ animationDelay: '300ms' }}>
              <span>14-day free trial</span>
              <div className="w-px h-4 bg-border" />
              <span>No credit card required</span>
              <div className="w-px h-4 bg-border" />
              <span>Cancel anytime</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-card/50 relative overflow-hidden">
        <div className="absolute inset-0 gradient-mesh opacity-50" />
        
        <div className="relative max-w-7xl mx-auto container-padding">
          <div className="text-center mb-16 animate-fade-in-up">
            <Badge variant="outline" className="mb-4 hover-lift">Features</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Everything You Need for CDRS
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              A complete toolkit designed specifically for developmental professionals
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="bg-background card-3d border-border/50 group animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardHeader className="pb-3">
                  <div className={`w-12 h-12 rounded-xl feature-icon flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle className="text-lg group-hover:text-primary transition-colors">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 gradient-mesh opacity-30" />
        
        <div className="relative max-w-7xl mx-auto container-padding">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left Content */}
            <div className="animate-slide-left">
              <Badge variant="outline" className="mb-4 hover-lift">Why CDRS</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">
                Built for Developmental Professionals
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                We understand the unique challenges of developmental assessment. 
                That's why we built CDRS from the ground up with your workflow in mind.
              </p>
              
              <div className="space-y-6">
                {benefits.map((benefit, index) => (
                  <div 
                    key={index} 
                    className="flex items-start gap-4 group hover-lift p-3 -ml-3 rounded-xl transition-all cursor-default"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 group-hover:scale-110 transition-all">
                      <benefit.icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">{benefit.title}</h3>
                      <p className="text-muted-foreground text-sm">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Right Content - Stats */}
            <div className="relative animate-slide-right">
              <div className="glass rounded-2xl p-8 shadow-xl border border-white/20">
                <h3 className="font-semibold text-foreground mb-6">Why Educators Choose CDRS</h3>
                
                <div className="space-y-4 mb-8">
                  {[
                    "Intuitive interface designed for busy professionals",
                    "Comprehensive assessment tracking and reporting",
                    "Dedicated Developmental Team ensuring accuracy",
                    "Regular updates based on user feedback",
                    "Secure cloud data storage",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 group">
                      <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-primary/20 transition-colors">
                        <CheckCircle className="w-3 h-3 text-primary" />
                      </div>
                      <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">{item}</span>
                    </div>
                  ))}
                </div>
                
                {/* Platform Features */}
                <div className="grid grid-cols-3 gap-4 pt-6 border-t border-border">
                  <div className="text-center">
                    <div className="text-lg font-semibold text-foreground">Secure</div>
                    <div className="text-xs text-muted-foreground">Cloud Storage</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-foreground">Fast</div>
                    <div className="text-xs text-muted-foreground">Assessment Tools</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-foreground">Reliable</div>
                    <div className="text-xs text-muted-foreground">24/7 Access</div>
                  </div>
                </div>
              </div>
              
              {/* Decorative */}
              <div className="absolute -z-10 -bottom-4 -right-4 w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 rounded-2xl animate-pulse" style={{ animationDuration: '4s' }} />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto container-padding">
          <div className="relative overflow-hidden rounded-3xl gradient-primary p-8 sm:p-12 lg:p-16 text-center">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2" />
              <div className="absolute bottom-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2" />
            </div>
            
            <div className="relative">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Ready to Transform Your Assessment Process?
              </h2>
              <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
                Start streamlining your developmental assessments today 
                with our comprehensive CDRS platform.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/signup">
                  <Button size="lg" variant="secondary" className="gap-2 btn-press text-base px-8">
                    Start Free Trial
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 text-base px-8">
                    View Pricing
                  </Button>
                </Link>
              </div>
              <p className="text-sm text-white/60 mt-6">
                14-day free trial • No credit card required • Cancel anytime
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-card border-t border-border">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="mb-4">
                <Logo size="sm" linkTo="/" />
              </div>
              <p className="text-sm text-muted-foreground max-w-sm">
                Streamline your developmental assessments with our comprehensive 
                CDRS platform. Built for professionals, by professionals.
              </p>
            </div>
            
            {/* Links */}
            <div>
              <h4 className="font-semibold text-foreground mb-3">Product</h4>
              <ul className="space-y-2">
                <li><Link to="/pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Pricing</Link></li>
                <li><a href="#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Features</a></li>
                <li><Link to="/signup" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Sign Up</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-foreground mb-3">Legal</h4>
              <ul className="space-y-2">
                <li><Link to="/terms-of-service" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Terms of Service</Link></li>
                <li><Link to="/privacy-policy" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="section-divider mb-8" />
          
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">
              © 2025 Child Development Reporting System. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Pricing
              </Link>
              <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
